// Pantalla: Login.
//
// El formulario ya no hace POST clasico de MVC: el submit se intercepta y las
// credenciales se mandan por fetch a AccountController.LoginJson, que crea la
// cookie de sesion y devuelve a donde redirigir. Ademas de eso: validacion
// antes de enviar, evitar doble submit, foco inicial y mostrar/ocultar
// contrasena.

import { byId } from '../core/dom';
import { post } from '../core/http';

/** Forma de la respuesta exitosa de AccountController.LoginJson. */
interface RespuestaLogin {
    redirectUrl: string;
}

const formulario = byId<HTMLFormElement>('formLogin');
const campoUsuario = byId<HTMLInputElement>('campoUsuario');
const campoPassword = byId<HTMLInputElement>('campoPassword');
const errorUsuario = byId('errorUsuario');
const errorPassword = byId('errorPassword');
const errorLogin = byId('errorLogin');
const mensajeErrorLogin = byId('mensajeErrorLogin');
const botonLogin = byId<HTMLButtonElement>('btnLogin');
const botonMostrarPassword = byId<HTMLButtonElement>('btnMostrarPassword');
const iconoMostrarPassword = byId('iconoMostrarPassword');

/**
 * Marca o limpia el estado invalido de un campo junto con su mensaje.
 *
 * Se usa "d-block" (en vez de confiar en el selector CSS de Bootstrap para
 * .invalid-feedback) porque el campo esta dentro de un .input-group: ahi el
 * hermano inmediato de la clase is-invalid no es el mensaje, y el selector
 * automatico de Bootstrap no lo mostraria.
 */
function marcarInvalido(campo: HTMLInputElement, mensaje: HTMLElement, invalido: boolean): void {
    campo.classList.toggle('is-invalid', invalido);
    mensaje.classList.toggle('d-block', invalido);
}

/** Valida que usuario y contrasena no esten vacios. Devuelve true si esta todo bien. */
function formularioValido(): boolean {
    const usuarioVacio = campoUsuario.value.trim() === '';
    const passwordVacio = campoPassword.value === '';

    marcarInvalido(campoUsuario, errorUsuario, usuarioVacio);
    marcarInvalido(campoPassword, errorPassword, passwordVacio);

    return !usuarioVacio && !passwordVacio;
}

/** Muestra (u oculta) el mensaje de error general del login. */
function mostrarErrorLogin(mensaje: string | null): void {
    errorLogin.classList.toggle('d-none', mensaje === null);
    mensajeErrorLogin.textContent = mensaje ?? '';
}

formulario.addEventListener('submit', (evento) => {
    // El submit ya no navega: todo el flujo se resuelve por fetch.
    evento.preventDefault();

    if (!formularioValido()) {
        return;
    }

    mostrarErrorLogin(null);

    // Deshabilitar el boton evita el doble submit mientras el servidor responde.
    // A diferencia del POST clasico de MVC, aca la pagina no se recarga sola, asi
    // que hay que volver a habilitarlo si la peticion falla (ver catch).
    botonLogin.disabled = true;

    void iniciarSesion();
});

async function iniciarSesion(): Promise<void> {
    try {
        const respuesta = await post<RespuestaLogin>('/api/account/login', {
            nombreUsuario: campoUsuario.value.trim(),
            contrasena: campoPassword.value
        });

        window.location.href = respuesta.redirectUrl;
    } catch (error) {
        // Se muestra el mensaje del servidor tal cual. core/http.ts solo convierte
        // un 401 en "sesion expirada" cuando viene con el codigo correspondiente,
        // que este endpoint no manda: su 401 es "credenciales incorrectas".
        const mensaje = error instanceof Error
            ? error.message
            : 'Error inesperado al iniciar sesión.';

        mostrarErrorLogin(mensaje);
        botonLogin.disabled = false;
    }
}

// Si el usuario corrige el campo, se le quita el estado invalido de una vez,
// sin esperar a un nuevo intento de envio.
campoUsuario.addEventListener('input', () => marcarInvalido(campoUsuario, errorUsuario, false));
campoPassword.addEventListener('input', () => marcarInvalido(campoPassword, errorPassword, false));

// Mostrar/ocultar la contrasena en texto plano.
botonMostrarPassword.addEventListener('click', () => {
    const vaAMostrarse = campoPassword.type === 'password';
    campoPassword.type = vaAMostrarse ? 'text' : 'password';
    iconoMostrarPassword.classList.toggle('fa-eye', !vaAMostrarse);
    iconoMostrarPassword.classList.toggle('fa-eye-slash', vaAMostrarse);
});

campoUsuario.focus();
